import CONSTANTS from 'tradera-constants/state';
import { cache, CACHE_KEYS } from 'tradera-utils/cache';
import actionTypes from './action-types';
import getInitialState from './initial-state';
import { getSystemMessageAlertCount } from './helpers';

const MESSAGING_NOTIFICATIONS_CACHE_TTL = 30 * 1000;

export default (state = {}, action) => {
    const toggleHeaderSearch = () => ({
        ...state,
        showHeaderSearch:
            action.payload === undefined
                ? !state.showHeaderSearch
                : !!action.payload
    });

    const openGdprModal = () => ({
        ...state,
        gdpr: {
            ...state.gdpr,
            modalOpen: true
        }
    });

    const closeGdprModal = () => ({
        ...state,
        gdpr: {
            ...state.gdpr,
            modalOpen: false
        }
    });

    const openGdprSettings = () => ({
        ...state,
        gdpr: {
            ...state.gdpr,
            settingsOpen: true
        }
    });

    const closeGdprSettings = () => ({
        ...state,
        gdpr: {
            ...state.gdpr,
            settingsOpen: false
        }
    });

    const openQuickView = () => {
        const {
            itemIds = state.quickView.itemIds,
            itemId = state.quickView.itemId
        } = action.payload;
        return {
            ...state,
            quickView: {
                ...state.quickView,
                open: CONSTANTS.MODAL.OPEN,
                itemIds: itemIds,
                currentItem: itemId
            }
        };
    };

    const closeQuickView = () => {
        return {
            ...state,
            quickView: {
                ...state.quickView,
                open: CONSTANTS.MODAL.CLOSED
            }
        };
    };

    const loadingQuickView = () => {
        return {
            ...state,
            quickView: {
                ...state.quickView,
                loading: action.payload
            }
        };
    };

    const openVideoModal = () => {
        const { videoUrl, title, pageType } = action.payload;
        return {
            ...state,
            videoModal: {
                ...state.videoModal,
                open: CONSTANTS.MODAL.OPEN,
                videoUrl: videoUrl,
                title: title,
                pageType: pageType
            }
        };
    };

    const closeVideoModal = () => {
        return {
            ...state,
            videoModal: {
                ...state.videoModal,
                open: CONSTANTS.MODAL.CLOSED
            }
        };
    };

    const openYoutubeVideoModal = () => {
        const { videoId, title, pageType } = action.payload;
        return {
            ...state,
            youtubeVideoModal: {
                ...state.youtubeVideoModal,
                open: CONSTANTS.MODAL.OPEN,
                videoId: videoId,
                title: title,
                pageType: pageType
            }
        };
    };

    const closeYoutubeVideoModal = () => {
        return {
            ...state,
            youtubeVideoModal: {
                ...state.youtubeVideoModal,
                open: CONSTANTS.MODAL.CLOSED
            }
        };
    };
    /*
        Changes made to messaging modal will affect My Tradera as well!
        TODO - test this!
    */
    const openMessagingModal = () => {
        const {
            title = state.messagingModal.title,
            src = state.messagingModal.src,
            participating = state.messagingModal.participating,
            recipientId = state.messagingModal.recipientId,
            item = state.messagingModal.item
        } = action.payload;
        return {
            ...state,
            messagingModal: {
                open: CONSTANTS.MODAL.OPEN,
                title: title,
                src: src,
                participating: participating,
                recipientId: recipientId,
                item: item
            }
        };
    };

    const closeMessagingModal = () => {
        return {
            ...state,
            messagingModal: {
                open: CONSTANTS.MODAL.CLOSED
            }
        };
    };

    const openNotificationOptinModal = () => {
        const {
            modalType,
            gaLabel,
            title,
            description,
            tagGroups
        } = action.payload;
        return {
            ...state,
            notificationOptinModal: {
                open: CONSTANTS.MODAL.OPEN,
                modalType,
                gaLabel,
                title,
                description,
                tagGroups
            }
        };
    };

    const closeNotificationOptinModal = () => {
        return {
            ...state,
            notificationOptinModal: {
                open: CONSTANTS.MODAL.CLOSED
            }
        };
    };

    const openModal = () => {
        const { modalId, data } = action.payload;
        return {
            ...state,
            modals: {
                currentModal: modalId,
                currentModalData: data
            }
        };
    };

    const closeModal = () => {
        return {
            ...state,
            modals: {
                currentModal: null,
                currentModalData: null
            }
        };
    };

    const itemLayout = () => {
        if (state.itemLayout === action.payload) {
            return state;
        } else {
            return {
                ...state,
                itemLayout: action.payload
            };
        }
    };

    const siteLayout = () => {
        if (state.siteLayout === action.payload) {
            return state;
        } else {
            return {
                ...state,
                siteLayout: action.payload
            };
        }
    };

    const updateSystemMessages = () => {
        return {
            ...state,
            systemMessages: {
                ...state.systemMessages,
                ...action.payload
            },
            badges: {
                ...state.badges,
                system: getSystemMessageAlertCount(action.payload)
            }
        };
    };

    const updateMessagingNotifications = () => {
        cache.write(
            CACHE_KEYS.MESSAGING_BADGES,
            action.payload,
            MESSAGING_NOTIFICATIONS_CACHE_TTL
        );
        return {
            ...state,
            badges: {
                ...state.badges,
                messaging: action.payload
            }
        };
    };

    const openProfileMenu = () => {
        const { payload } = action;
        return {
            ...state,
            profileMenu: {
                open: CONSTANTS.MODAL.OPEN,
                redirectUrl: payload || false
            }
        };
    };

    const closeProfileMenu = () => {
        return {
            ...state,
            profileMenu: {
                ...state.profileMenu,
                open: CONSTANTS.MODAL.CLOSED
            }
        };
    };

    const openCategoryDropdownMenu = () => {
        return {
            ...state,
            categoryDropdownMenu: {
                open: CONSTANTS.MODAL.OPEN
            }
        };
    };

    const closeCategoryDropdownMenu = () => {
        return {
            ...state,
            categoryDropdownMenu: {
                open: CONSTANTS.MODAL.CLOSED
            }
        };
    };

    const openSearchForm = () => {
        return {
            ...state,
            showSearchForm: true
        };
    };

    const closeSearchForm = () => {
        return {
            ...state,
            showSearchForm: false
        };
    };

    const logOut = () => {
        const { badges } = getInitialState({});
        return {
            ...state,
            badges
        };
    };

    const openItemSettings = () => {
        const { payload } = action;
        return {
            ...state,
            itemSettings: {
                open: CONSTANTS.MODAL.OPEN,
                ...payload
            }
        };
    };

    const closeItemSettings = () => {
        return {
            ...state,
            itemSettings: {
                open: CONSTANTS.MODAL.CLOSED,
                item: {}
            }
        };
    };

    const openShareModal = () => ({
        ...state,
        shareModal: {
            isOpen: true,
            settings: action.payload
        }
    });

    const initialize = () => {
        const { payload } = action;
        return {
            ...payload
        };
    };

    const closeShareModal = () => ({
        ...state,
        shareModal: {
            isOpen: false,
            settings: null
        }
    });

    const def = () => state;

    const reducers = {
        [actionTypes.INITIALIZE]: initialize,
        [actionTypes.TOGGLE_HEADER_SEARCH]: toggleHeaderSearch,
        [actionTypes.OPEN_GDPR_MODAL]: openGdprModal,
        [actionTypes.CLOSE_GDPR_MODAL]: closeGdprModal,
        [actionTypes.OPEN_GDPR_SETTINGS]: openGdprSettings,
        [actionTypes.CLOSE_GDPR_SETTINGS]: closeGdprSettings,
        [actionTypes.OPEN_QUICK_VIEW]: openQuickView,
        [actionTypes.CLOSE_QUICK_VIEW]: closeQuickView,
        [actionTypes.LOADING_QUICK_VIEW]: loadingQuickView,
        [actionTypes.OPEN_VIDEO_MODAL]: openVideoModal,
        [actionTypes.CLOSE_VIDEO_MODAL]: closeVideoModal,
        [actionTypes.OPEN_YOUTUBE_VIDEO_MODAL]: openYoutubeVideoModal,
        [actionTypes.CLOSE_YOUTUBE_VIDEO_MODAL]: closeYoutubeVideoModal,
        [actionTypes.OPEN_MESSAGING_MODAL]: openMessagingModal,
        [actionTypes.CLOSE_MESSAGING_MODAL]: closeMessagingModal,
        [actionTypes.OPEN_NOTIFICATION_OPTIN_MODAL]: openNotificationOptinModal,
        [actionTypes.CLOSE_NOTIFICATION_OPTIN_MODAL]: closeNotificationOptinModal,
        [actionTypes.ITEM_LAYOUT]: itemLayout,
        [actionTypes.UPDATE_SYSTEM_MESSAGES]: updateSystemMessages,
        [actionTypes.UPDATE_MESSAGING_NOTIFICATIONS]: updateMessagingNotifications,
        [actionTypes.OPEN_PROFILE_MENU]: openProfileMenu,
        [actionTypes.CLOSE_PROFILE_MENU]: closeProfileMenu,
        [actionTypes.OPEN_CATEGORY_DROPDOWN_MENU]: openCategoryDropdownMenu,
        [actionTypes.CLOSE_CATEGORY_DROPDOWN_MENU]: closeCategoryDropdownMenu,
        [actionTypes.OPEN_SEARCH_FORM]: openSearchForm,
        [actionTypes.CLOSE_SEARCH_FORM]: closeSearchForm,
        [actionTypes.OPEN_ITEM_SETTINGS]: openItemSettings,
        [actionTypes.CLOSE_ITEM_SETTINGS]: closeItemSettings,
        [actionTypes.SITE_LAYOUT]: siteLayout,
        [actionTypes.LOG_OUT]: logOut,
        [actionTypes.OPEN_MODAL]: openModal,
        [actionTypes.CLOSE_MODAL]: closeModal,
        [actionTypes.OPEN_SHARE_MODAL]: openShareModal,
        [actionTypes.CLOSE_SHARE_MODAL]: closeShareModal
    };

    return !action || !action.type || !reducers[action.type]
        ? def()
        : reducers[action.type]();
};
